import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type {
  DestinationRow,
  OrganizationOption,
  RuntimeDestination,
} from "./types";

interface Props {
  destination: DestinationRow | null | undefined;
  runtimeDestinations: RuntimeDestination[];
  organizations: OrganizationOption[];
  copy: Record<string, string>;
  onClose: () => void;
}

const empty = {
  name: "",
  slug: "",
  kind: "clickhouse",
  environment: "production",
  ownership: "stationzero_managed",
  runtime_destination_id: "",
  region: "",
  description: "",
  enabled: true,
  organization_ids: [] as string[],
  global_default: false,
};

export default function DestinationDialog({
  destination,
  runtimeDestinations,
  organizations,
  copy,
  onClose,
}: Props) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(
      destination
        ? {
            name: destination.name,
            slug: destination.slug,
            kind: destination.kind,
            environment: destination.environment,
            ownership: destination.ownership,
            runtime_destination_id: destination.runtime_destination_id || "",
            region: destination.region || "",
            description: destination.description || "",
            enabled: destination.enabled,
            organization_ids: destination.organization_ids,
            global_default: destination.global_default,
          }
        : empty,
    );
  }, [destination]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const url = destination
      ? `/cc/forge_destinations/${destination.id}`
      : "/cc/forge_destinations";
    const options = { onSuccess: onClose, onFinish: () => setSaving(false) };
    const data = { forge_destination: form };
    if (destination) router.patch(url, data, options);
    else router.post(url, data, options);
  };

  const selectRuntime = (runtimeId: string) => {
    const runtime = runtimeDestinations.find((item) => item.id === runtimeId);
    setForm((current) => ({
      ...current,
      runtime_destination_id: runtimeId,
      name: current.name || runtime?.name || "",
      kind: runtime?.kind || current.kind,
    }));
  };

  return (
    <Dialog
      open={destination !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {destination ? copy.edit_title : copy.new_title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-sm font-medium">{copy.name}</span>
              <Input
                required
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
              />
            </label>
            <label className="space-y-1">
              <span className="text-sm font-medium">{copy.slug}</span>
              <Input
                value={form.slug}
                onChange={(event) =>
                  setForm({ ...form, slug: event.target.value })
                }
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">
              {copy.runtime_destination}
            </span>
            <select
              required
              value={form.runtime_destination_id}
              onChange={(event) => selectRuntime(event.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="" disabled>
                {copy.select_runtime}
              </option>
              {runtimeDestinations.map((runtime) => (
                <option key={runtime.id} value={runtime.id}>
                  {runtime.name} · {runtime.kind || copy.unknown}
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="space-y-1">
              <span className="text-sm font-medium">{copy.kind}</span>
              <select
                value={form.kind}
                onChange={(event) =>
                  setForm({ ...form, kind: event.target.value })
                }
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {[
                  "clickhouse",
                  "s3",
                  "postgres",
                  "snowflake",
                  "bigquery",
                  "other",
                ].map((kind) => (
                  <option key={kind}>{kind}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-sm font-medium">{copy.environment}</span>
              <select
                value={form.environment}
                onChange={(event) =>
                  setForm({ ...form, environment: event.target.value })
                }
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {["production", "staging", "local"].map((environment) => (
                  <option key={environment}>{environment}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-sm font-medium">{copy.ownership}</span>
              <select
                value={form.ownership}
                onChange={(event) =>
                  setForm({ ...form, ownership: event.target.value })
                }
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="stationzero_managed">
                  {copy.stationzero_managed}
                </option>
                <option value="customer_owned">{copy.customer_owned}</option>
              </select>
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">{copy.region}</span>
            <Input
              value={form.region}
              onChange={(event) =>
                setForm({ ...form, region: event.target.value })
              }
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">{copy.notes}</span>
            <textarea
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <fieldset className="rounded-lg border border-border p-3">
            <legend className="px-1 text-sm font-medium">
              {copy.organizations}
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {organizations.map((organization) => (
                <label
                  key={organization.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={form.organization_ids.includes(organization.id)}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        organization_ids: event.target.checked
                          ? [...form.organization_ids, organization.id]
                          : form.organization_ids.filter(
                              (id) => id !== organization.id,
                            ),
                      })
                    }
                  />
                  {organization.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) =>
                  setForm({ ...form, enabled: event.target.checked })
                }
              />
              {copy.enabled}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.global_default}
                onChange={(event) =>
                  setForm({ ...form, global_default: event.target.checked })
                }
              />
              {copy.global_default}
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {copy.cancel}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? copy.saving : copy.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
